/*
Watch-segment builder (SP-2690): turns the player's raw progress observations
into contiguous WALL-CLOCK playback segments.

The player reports the playhead on every timeupdate while actually playing
(OwnedFullPlayer.handleTimeUpdate guards `el.paused`), and also on seeks —
including paused scrubs. This module distinguishes real playback from those
one-off reports with a coherence rule: an observation extends the open
segment only when the playhead advanced by roughly (wall-clock elapsed ×
playback rate). Everything else — seeks, rate changes, video switches, quiet
(pause fires no observations at all) — closes the segment.

Framework-free and clock-injectable, mirroring videoEngineCore: all timing
comes through `now()` and the armed quiet timer, so tests drive it
deterministically.

Segment identity: the id is minted ONCE when a segment opens and carried
through every extension and queued resend. It is never re-derived — a
re-derived id on a resend would file one watched stretch under two rows
(the 08-26 incident class: identity minted at use time instead of recorded
at creation time).
*/

/** Opaque quiet-timer handle (number in browsers, Timeout under bun/node). */
export type TimerHandle = unknown;

export interface WatchSegment {
  segmentId: string;
  videoId: string;
  /** Client-clock epoch ms. */
  startedAtMs: number;
  endedAtMs: number;
  playheadStartSeconds: number;
  playheadEndSeconds: number;
  /** Constant within a segment; a rate change closes it. */
  playbackRate: number;
  /**
   * Whether the page was visible while this segment accrued. Constant within
   * a segment: a visibility change splits the segment seamlessly (the
   * successor inherits the boundary playhead), so locked-screen/background
   * listening lands in its own rows and "eyes-on" vs "audio-only" time are
   * separable in the metric.
   */
  screenVisible: boolean;
  observationCount: number;
}

export interface WatchSegmentBuilderOptions {
  /**
   * Called when the open segment should be (re-)enqueued: once it has
   * accrued `MIN_EMIT_WALL_MS` of watched time, then at most every
   * `queueUpdateIntervalMs` while it grows, and finally on close. A segment
   * closed before reaching the minimum is dropped, never emitted. Consumers
   * upsert by segmentId, so calling with the same segment repeatedly is
   * idempotent-by-design.
   */
  onSegment: (segment: WatchSegment) => void;
  generateSegmentId: () => string;
  now?: () => number;
  /** Test seam; production uses real timers for the quiet-close. */
  setTimeoutFn?: (fn: () => void, ms: number) => TimerHandle;
  clearTimeoutFn?: (handle: TimerHandle) => void;
  /** How often a growing segment refreshes its queue entry. */
  queueUpdateIntervalMs?: number;
  /**
   * Sampled when a segment opens (splits read it via noteVisibility).
   * Defaults to visible — non-browser callers and tests record eyes-on.
   */
  getScreenVisible?: () => boolean;
  /**
   * No observation for this long closes the segment — a FOREGROUND liveness
   * cleanup only, not the pause detector (explicit boundaries close on
   * pause/seek/ended) and not an accrual risk (duration accrues from the
   * media clock, so silence adds nothing). A page-hide checkpoint disarms
   * it: hidden pages can suspend delivery for minutes while audio genuinely
   * plays, and the returning observation's playhead advance proves the
   * playback whatever the gap. Coherence itself accepts any gap length for
   * the same reason — the timer exists only so a foreground stop that
   * somehow skipped its explicit boundary still flushes its tail.
   */
  quietCloseMs?: number;
}

const DEFAULT_QUEUE_UPDATE_INTERVAL_MS = 15_000;
const DEFAULT_QUIET_CLOSE_MS = 30_000;

// Two coherent observations confirm playback, but the player fires its first
// timeupdates within a millisecond of each other at play start, so a
// segment can be confirmed with under 1ms accrued and then closed by the
// resume seek that follows — persisting a row of no measurable watching for
// every playback start. Nothing is enqueued until this much watched time
// has accrued; a segment that closes short of it is dropped. The crash-loss
// bound this sets (under a second of real watching) is below the metric's
// resolution.
export const MIN_EMIT_WALL_MS = 1_000;

// During real playback the playhead IS a wall clock: it advances at exactly
// (elapsed wall × rate). The DELIVERY time of an observation is not — under
// main-thread jank (parallel test workers, GC, a busy tab) timeupdate
// handlers are delayed and then delivered in bursts, so delivery deltas stop
// matching playhead deltas even though playback never hiccupped. Segments
// therefore measure wall time from the MEDIA clock (accumulated
// playheadΔ ÷ rate) and use delivery time only for two things:
//   - liveness: a delivery silence longer than the quiet window closes the
//     segment (pause reports nothing; the provider's explicit
//     pause/seek/ended closes remain the primary boundaries);
//   - seek detection: the playhead can never legitimately advance by MORE
//     wall time than actually elapsed, so playheadΔ÷rate exceeding the
//     delivery delta (plus a burst allowance) is a forward jump, not
//     playback.
// Consequences, all in the metric's favor: durations cannot be inflated by
// buffering stalls, silent pauses, or delivery jank (their wall time simply
// is not accrued — at any playback rate, by construction), and a
// jank-starved observation stream degrades to splitting segments rather
// than losing or inventing time.
//
// Semantics note: endedAt therefore means startedAt + WATCHED duration, not
// "the wall moment playback stopped" — mid-segment stalls make it lag real
// time by the stalled seconds (bounded by the liveness window per gap).
// Time-bucketed queries anchor on startedAt; the lag only nudges which
// bucket a segment's END lands in, by seconds in practice, and trading that
// for un-inflatable durations is deliberate.
const SEEK_DETECTION_SLACK_WALL_SECONDS = 0.5;

interface OpenSegmentState {
  segment: WatchSegment;
  /** Wall time of the most recent observation (segment.endedAtMs mirrors it). */
  lastObservationMs: number;
  lastPlayheadSeconds: number;
  /** False until a second coherent observation confirms real playback. */
  confirmed: boolean;
  /** Null until the segment has actually been enqueued once. */
  lastQueueUpdateMs: number | null;
  quietTimer: TimerHandle | null;
}

export interface WatchSegmentBuilder {
  /** Feed one raw progress observation (every player timeupdate/seek report). */
  observe(videoId: string, playheadSeconds: number, playbackRate: number): void;
  /**
   * Explicit playback boundary for ONE video (the player's pause/seek/ended
   * flush). Scoped: with several players mounted, one player's boundary must
   * not end another video's live segment.
   */
  close(videoId: string): void;
  /**
   * The page's visibility changed. If a confirmed segment is open, split it
   * here: close it at its current state and continue in a NEW segment that
   * inherits the boundary (same rate, start = predecessor's end, playhead
   * carried forward) so content played across the boundary — including a
   * whole locked stretch whose observations arrive only on resume — accrues
   * into the correctly-labeled successor rather than being dropped.
   * Unconfirmed candidates are PRESERVED — they hold the only
   * pre-boundary playhead anchor, and discarding one at a lock would lose
   * the whole hidden stretch that follows. On hide they are relabeled
   * hidden (zero accrual so far, so the label describes what accrues
   * next); on show they keep the hidden label, and the derived split
   * continues visible after the confirming observation lands.
   */
  noteVisibility(visible: boolean): void;
  /**
   * Emit the open segment's current state to the queue WITHOUT closing it
   * — durability for a possible kill while hidden (the enqueued row
   * upserts under its segment's id, so checkpointing is free). The
   * page-hide caller pairs this with noteVisibility(false): checkpoint
   * first flushes the visible segment's final state, the split then routes
   * hidden playback (locked-screen background audio, which deliberately
   * counts) into an audio-only successor. Also disarms the quiet timer: a
   * hidden page can suspend observation delivery for minutes while audio
   * genuinely plays, and the next observation's playhead advance proves
   * the playback regardless of the gap (accrual is media-clock, seeks
   * close via the player's explicit seeking signal — a long silent gap
   * corrupts nothing). The timer re-arms on the next observation.
   */
  checkpointAll(): void;
  /** Close and emit whatever is open (teardown). */
  closeAll(): void;
}

export function createWatchSegmentBuilder(
  options: WatchSegmentBuilderOptions,
): WatchSegmentBuilder {
  const now = options.now ?? Date.now;
  const setTimeoutFn: (fn: () => void, ms: number) => TimerHandle =
    options.setTimeoutFn ?? ((fn, ms) => setTimeout(fn, ms));
  const clearTimeoutFn: (handle: TimerHandle) => void =
    options.clearTimeoutFn ??
    ((handle) => clearTimeout(handle as Parameters<typeof clearTimeout>[0]));
  const queueUpdateIntervalMs =
    options.queueUpdateIntervalMs ?? DEFAULT_QUEUE_UPDATE_INTERVAL_MS;
  const quietCloseMs = options.quietCloseMs ?? DEFAULT_QUIET_CLOSE_MS;
  const getScreenVisible = options.getScreenVisible ?? (() => true);

  // One CONFIRMED segment at a time (two videos genuinely playing at once is
  // not a user scenario), plus per-video unconfirmed candidates. Candidates
  // exist so that with several players mounted, a foreign video's lone
  // report — a paused scrub on another player, an unmount flush — cannot end
  // the live segment: another video displaces the confirmed segment only by
  // proving real playback (two coherent observations of its own).
  let open: OpenSegmentState | null = null;
  const candidates = new Map<string, OpenSegmentState>();
  // Show-direction visibility splits are DEFERRED to the next observation:
  // on unlock, visibilitychange fires BEFORE delivery resumes, and the
  // resume observation carries the whole locked stretch's playhead delta —
  // splitting at the event would re-anchor first and then read that delta
  // as a forward seek, dropping the locked listening entirely. Rather than
  // a stored pending flag, the split condition is DERIVED where the
  // observation lands (label ≠ getScreenVisible()), so it also covers a
  // candidate that confirms across the boundary and cancels itself when a
  // re-hide makes the labels agree again.

  function disarmQuietTimer() {
    if (open?.quietTimer != null) {
      clearTimeoutFn(open.quietTimer);
      open.quietTimer = null;
    }
  }

  function armQuietTimer() {
    if (open == null) {
      return;
    }

    disarmQuietTimer();
    open.quietTimer = setTimeoutFn(() => {
      // No observations for the whole quiet window: playback stopped without
      // a terminal report (pause, tab hidden, app backgrounded without
      // audio). The segment honestly ends at its LAST observation.
      closeOpenSegment();
    }, quietCloseMs);
  }

  function emit(state: OpenSegmentState) {
    // Every emit path (confirmation, cadence, checkpoint, close) shares this
    // gate, so a segment that never reaches the minimum reaches no consumer.
    if (
      state.segment.endedAtMs - state.segment.startedAtMs <
      MIN_EMIT_WALL_MS
    ) {
      return;
    }

    state.lastQueueUpdateMs = state.lastObservationMs;
    options.onSegment({ ...state.segment });
  }

  function closeOpenSegment() {
    if (open == null) {
      return;
    }

    disarmQuietTimer();
    const state = open;
    open = null;
    emit(state);
  }

  function makeCandidate(
    videoId: string,
    playheadSeconds: number,
    playbackRate: number,
  ): OpenSegmentState {
    const startMs = now();
    return {
      segment: {
        segmentId: options.generateSegmentId(),
        videoId,
        startedAtMs: startMs,
        endedAtMs: startMs,
        playheadStartSeconds: playheadSeconds,
        playheadEndSeconds: playheadSeconds,
        playbackRate,
        screenVisible: getScreenVisible(),
        observationCount: 1,
      },
      lastObservationMs: startMs,
      lastPlayheadSeconds: playheadSeconds,
      confirmed: false,
      lastQueueUpdateMs: null,
      quietTimer: null,
    };
  }

  function observationCoheres(
    state: OpenSegmentState,
    nowMs: number,
    playheadSeconds: number,
    playbackRate: number,
  ): boolean {
    if (playbackRate !== state.segment.playbackRate) {
      return false;
    }

    const wallSeconds = (nowMs - state.lastObservationMs) / 1000;

    if (wallSeconds < 0) {
      return false;
    }

    const playheadDelta = playheadSeconds - state.lastPlayheadSeconds;

    if (playheadDelta <= 0) {
      return false;
    }

    // Forward-seek detection: played wall time can never exceed elapsed
    // delivery time (the burst allowance covers queued handlers draining
    // together). Slow delivery of slow progress is fine — the accrual only
    // credits the playhead movement anyway.
    const impliedWallSeconds = playheadDelta / playbackRate;
    return (
      impliedWallSeconds <= wallSeconds + SEEK_DETECTION_SLACK_WALL_SECONDS
    );
  }

  function extendState(
    state: OpenSegmentState,
    nowMs: number,
    playheadSeconds: number,
  ) {
    // Media-clock accrual: the segment grows by the wall time the playhead
    // movement proves, not by the delivery gap (see the header comment).
    const playedWallMs =
      ((playheadSeconds - state.lastPlayheadSeconds) /
        state.segment.playbackRate) *
      1000;
    state.segment.endedAtMs += playedWallMs;
    state.segment.playheadEndSeconds = playheadSeconds;
    state.segment.observationCount += 1;
    state.lastObservationMs = nowMs;
    state.lastPlayheadSeconds = playheadSeconds;
  }

  // After an observation has accrued into `open`, realign its label with
  // the CURRENT visibility: a disagreement means a show-direction flip
  // happened while delivery was suspended (or a candidate confirmed across
  // the boundary), and the just-accrued stretch belongs to the old label —
  // so split only now, never at the event.
  function splitIfLabelStale() {
    if (open != null && open.segment.screenVisible !== getScreenVisible()) {
      splitOpenSegment(getScreenVisible());
    }
  }

  function splitOpenSegment(visible: boolean) {
    if (open == null) {
      return;
    }

    const closed = open;
    closeOpenSegment();
    // Seamless successor: confirmed from birth, anchored at the
    // predecessor's media-clock end so nothing is lost at the boundary.
    open = {
      segment: {
        segmentId: options.generateSegmentId(),
        videoId: closed.segment.videoId,
        startedAtMs: closed.segment.endedAtMs,
        endedAtMs: closed.segment.endedAtMs,
        playheadStartSeconds: closed.segment.playheadEndSeconds,
        playheadEndSeconds: closed.segment.playheadEndSeconds,
        playbackRate: closed.segment.playbackRate,
        screenVisible: visible,
        observationCount: 0,
      },
      // The predecessor's delivery anchor, NOT now(): a hide event can fire
      // hundreds of ms after the last delivered timeupdate (main-thread
      // jank), and the next report still carries the playhead delta earned
      // since that anchor. Re-anchoring to the event time would shrink the
      // wall window under that delta and read it as a seek, dropping
      // content at the boundary.
      lastObservationMs: closed.lastObservationMs,
      lastPlayheadSeconds: closed.lastPlayheadSeconds,
      confirmed: true,
      lastQueueUpdateMs: null,
      quietTimer: null,
    };

    // A hidden successor's delivery can suspend indefinitely (that is the
    // point of the split); its timer stays disarmed until an observation
    // arrives, exactly as after a checkpoint.
    if (visible) {
      armQuietTimer();
    }
  }

  return {
    observe(videoId, playheadSeconds, playbackRate) {
      const nowMs = now();

      // A repeat of the live segment's current playhead (queued handlers
      // draining in a burst all read the SAME live currentTime; seek
      // settles re-report the position) carries no information: not
      // progress, not a boundary. Ignoring it — rather than treating it as
      // incoherent — is what keeps a jank burst from splitting a segment.
      if (
        open != null &&
        open.segment.videoId === videoId &&
        playbackRate === open.segment.playbackRate &&
        playheadSeconds === open.lastPlayheadSeconds
      ) {
        return;
      }

      // Extends the live segment?
      if (
        open != null &&
        open.segment.videoId === videoId &&
        observationCoheres(open, nowMs, playheadSeconds, playbackRate)
      ) {
        extendState(open, nowMs, playheadSeconds);
        splitIfLabelStale();
        armQuietTimer();

        // A segment confirmed below the emit minimum enqueues on the first
        // extension that crosses it, then at the queue cadence.
        if (
          open.lastQueueUpdateMs == null ||
          nowMs - open.lastQueueUpdateMs >= queueUpdateIntervalMs
        ) {
          emit(open);
        }

        return;
      }

      // Confirms (or restarts) a candidate? Candidates are per video, so a
      // foreign player's lone reports accumulate against their OWN video and
      // never touch the live segment until they prove real playback.
      const candidate = candidates.get(videoId);

      if (
        candidate != null &&
        observationCoheres(candidate, nowMs, playheadSeconds, playbackRate)
      ) {
        extendState(candidate, nowMs, playheadSeconds);
        // Real playback confirmed here: whatever was live before has been
        // displaced (same video after a seek/rate change, or another video
        // that actually started playing).
        candidates.delete(videoId);
        closeOpenSegment();
        candidate.confirmed = true;
        open = candidate;
        armQuietTimer();
        // Enqueue as soon as the emit minimum allows, so a crash right after
        // loses at most one queue-update window, not the whole segment.
        emit(open);
        // A candidate can confirm ACROSS a visibility boundary (opened
        // pre-lock, confirmed by the resume observation): the accrued
        // stretch carries its birth label; continue under the current one.
        splitIfLabelStale();
        return;
      }

      // Neither: this report starts a fresh candidate for its video. If the
      // report belongs to the LIVE video (a seek jump, a rate change), the
      // live segment ends now — its playback demonstrably stopped. A foreign
      // video's report leaves the live segment alone.
      if (open != null && open.segment.videoId === videoId) {
        closeOpenSegment();
      }

      candidates.set(
        videoId,
        makeCandidate(videoId, playheadSeconds, playbackRate),
      );
    },

    noteVisibility(visible) {
      // Candidates are PRESERVED across the flip — deleting one would throw
      // away the only pre-boundary playhead anchor, so a lock landing in
      // the tiny window between a segment's first and second observation
      // would lose the entire hidden stretch that follows (Codex P2 on
      // #3976). On hide, relabel them hidden: their accrual is still zero,
      // so the label describes only what accrues next. On show, keep the
      // hidden label — the stretch up to the confirming observation is the
      // suspended-delivery gap, and the derived split continues visible
      // after it lands. Both directions bias toward audio-only by at most
      // one observation gap, same as the open-segment boundaries.
      if (!visible) {
        for (const candidate of candidates.values()) {
          candidate.segment.screenVisible = false;
        }
      }

      if (open == null || open.segment.screenVisible === visible) {
        return;
      }

      if (visible) {
        // Unlock/foreground: delivery may have been suspended the whole
        // hidden stretch, and its playhead delta arrives only with the
        // NEXT observation — which must accrue into the hidden segment
        // BEFORE the split (Cursor High on #3976). The derived
        // splitIfLabelStale handles it where that observation lands;
        // re-arm the quiet timer so an unlock that never resumes playback
        // still closes the hidden segment.
        armQuietTimer();
        return;
      }

      // Hide: observations were flowing until this moment (the segment is
      // visible and live), so the accrued state is current — split now.
      // The at-most-one-timeupdate sliver between the last observation and
      // the flip lands in the hidden successor; the same-sized asymmetry
      // exists on show, biasing both boundaries toward audio-only by a
      // fraction of a second.
      splitOpenSegment(visible);
    },

    close(videoId) {
      candidates.delete(videoId);

      if (open?.segment.videoId === videoId) {
        closeOpenSegment();
      }
    },

    checkpointAll() {
      if (open != null) {
        emit(open);
        disarmQuietTimer();
      }
    },

    closeAll() {
      candidates.clear();
      closeOpenSegment();
    },
  };
}
